/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { DialogOpenEvent } from '@theme/dialog';

/**
 * Lazy-mounts Yotpo's reviews widget the first time the reviews drawer opens.
 *
 * Expected markup (rendered by `snippets/reviews-drawer.liquid`):
 *
 * ```html
 * <dialog-component id="reviews-drawer-…">
 *   <dialog>
 *     <yotpo-reviews-widget>
 *       <template>
 *         <div class="yotpo-widget-instance" data-yotpo-instance-id="…" …></div>
 *       </template>
 *     </yotpo-reviews-widget>
 *   </dialog>
 * </dialog-component>
 * ```
 *
 * The widget markup sits inside a `<template>` so Yotpo's sitewide loader
 * script (app embed via `content_for_header`) does NOT find it on the initial
 * page scan. Otherwise every PDP pays for third-party JS, network and
 * main-thread work to render a panel most visitors never open.
 *
 * On the drawer's first `dialog:open` the template is stamped into place and
 * the loader is asked to process the new node. Two wrinkles it has to handle:
 *
 *  1. Yotpo's loader is async and may not have run yet, so `#initYotpoWithRetry`
 *     retries the init call up to 15 × 200ms.
 *  2. Colourway swaps on a combined-listing PDP go through `variant-picker.js`'s
 *     `updateMain()`, which DOM-morphs `<main>` in place (see `assets/morph.js`).
 *     Neither this element nor the widget div carries an `id` (morph key), so the
 *     keyed diff patches `data-yotpo-*` on the *existing* live nodes instead of
 *     re-inserting them — `connectedCallback` does NOT reliably fire again. A
 *     MutationObserver on `data-yotpo-product-id` (mirroring the pattern in
 *     `ingrid-pdp-widget.js`) catches that; Yotpo's loader has no observer of
 *     its own, so nothing else would notice the swap.
 */
class YotpoReviewsWidget extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {Element | null} */
  #dialogComponent = null;
  /** @type {boolean} */
  #mounted = false;

  connectedCallback() {
    this.#dialogComponent = this.closest('dialog-component');
    // `dialog:open` fires on the <dialog-component> itself and does not bubble.
    this.#dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
  }

  disconnectedCallback() {
    this.#dialogComponent?.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleDialogOpen = () => {
    this.#mount();
  };

  #mount() {
    if (this.#mounted) {
      // Already stamped: a re-open only needs a nudge, since a closed dialog is
      // not laid out and the widget can otherwise measure zero width.
      this.#initYotpoWithRetry(15);
      return;
    }

    const template = this.querySelector(':scope > template');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.#mounted = true;
    template.replaceWith(template.content.cloneNode(true));

    this.#observer = new MutationObserver(() => this.#initYotpoWithRetry(15));
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-yotpo-product-id'],
      subtree: true,
    });

    this.#initYotpoWithRetry(15);
  }

  /** @param {number} attemptsLeft */
  #initYotpoWithRetry(attemptsLeft) {
    if (this.#tryInitYotpo()) return;
    if (attemptsLeft <= 0) return;
    setTimeout(() => this.#initYotpoWithRetry(attemptsLeft - 1), 200);
  }

  /**
   * @returns {boolean} `true` if a Yotpo init API was reachable.
   *
   * Calls both APIs, not just the first that exists: `initWidgets()` skips
   * elements already marked `data-yotpo-element-loaded`, so on a colourway swap
   * `refreshWidgets()` is the one that does the work, and returning early on
   * `initWidgets` merely being callable would skip it.
   */
  #tryInitYotpo() {
    try {
      // Yotpo's global loader defines these on `window` at runtime — not part of
      // the theme's typed globals (`global.d.ts`), so cast locally rather than
      // adding a one-off third-party type there.
      const win = /** @type {any} */ (window);
      let reached = false;

      if (typeof win.yotpoWidgetsContainer?.initWidgets === 'function') {
        win.yotpoWidgetsContainer.initWidgets();
        reached = true;
      }
      if (typeof win.yotpo?.refreshWidgets === 'function') {
        win.yotpo.refreshWidgets();
        reached = true;
      }

      return reached;
    } catch (error) {
      console.error('[yotpo-reviews-widget] init failed:', error);
    }
    return false;
  }
}

if (!customElements.get('yotpo-reviews-widget')) {
  customElements.define('yotpo-reviews-widget', YotpoReviewsWidget);
}
